import { useState } from "react";
import { format, parseISO } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

// Gold-on-navy date picker matching the HYLUNER aesthetic. Value is an ISO date string.
export const DatePicker = ({ value, onChange, placeholder = "Pick a date", testId = "date-picker" }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="hy-input flex items-center justify-between gap-3 text-left">
          <span className={selected ? "text-slate-100" : "text-slate-500"}>
            {selected ? format(selected, "d MMMM yyyy") : placeholder}
          </span>
          <CalendarDays className="w-4 h-4 shrink-0" style={{ color: "var(--hy-gold)" }} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 border-amber-500/30 bg-[#0b0f19]"
        data-testid={`${testId}-popover`}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => {
            onChange(d ? format(d, "yyyy-MM-dd") : "");
            setOpen(false);
          }}
          classNames={{
            caption_label: "text-sm font-medium text-amber-200 tracking-wide",
            head_cell: "text-slate-500 w-9 font-normal text-[0.7rem] uppercase tracking-widest",
            nav_button: "h-7 w-7 border border-amber-500/30 text-amber-200 hover:bg-amber-500/10 rounded-sm",
            day: "h-9 w-9 p-0 font-normal text-slate-300 hover:bg-amber-500/10 hover:text-amber-100 rounded-sm transition-colors",
            day_selected: "bg-amber-400 text-[#080c16] hover:bg-amber-400 hover:text-[#080c16] focus:bg-amber-400",
            day_today: "border border-amber-500/40 text-amber-200",
            day_outside: "text-slate-600",
          }}
        />
        {value && (
          <button type="button" onClick={() => { onChange(""); setOpen(false); }}
            className="w-full py-3 text-[0.6rem] uppercase tracking-widest text-slate-400 hover:text-amber-200 border-t border-amber-500/15"
            data-testid={`${testId}-clear`}>
            Clear date
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};
